import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import {
  EditTrademarkFamilyDialogComponent,
} from 'src/app/dialogs/edit-trademark-family-dialog/edit-trademark-family-dialog.component';
import { ProductService } from 'src/app/models/productService';
import { PropertyOffice } from 'src/app/models/propertyOffice';
import {
  SynchronizationCandidate,
  Trademark,
  TrademarkStatus,
  TrademarkSynchronizationStatus,
  TrademarkType,
} from 'src/app/models/trademark';
import { TrademarkFamily } from 'src/app/models/trademarkFamily';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import {
  ProductServiceService,
} from 'src/app/services/product-service.service';
import {
  PropertyOfficeService,
} from 'src/app/services/property-office.service';
import { TerritoryService } from 'src/app/services/territory.service';
import {
  TrademarkFamilyService,
} from 'src/app/services/trademark-family.service';
import { TrademarkService } from 'src/app/services/trademark.service';
import { Option } from '../select/select.component';
import {
  NiceClass,
  SYNCHRONIZED_FIELDS,
  SynchronizedField,
  hasWording,
  synchronizationFailureKey,
  synchronizedValues,
} from 'src/app/helpers/trademark-synchronization.helper';

type ComparisonKind = 'text' | 'status' | 'office' | 'classes' | 'date' | 'image';

interface ClassComparison {
  classNumber: number;
  change: 'ADDED' | 'REMOVED' | 'WORDING' | 'WORDING_KEPT';
  old?: string;
  new?: string;
}

// A field a synchronisation writes, or keeps because the office gives no value for it
interface Difference {
  property: SynchronizedField;
  label: string;
  kind: ComparisonKind;
  old: any;
  new: any;
  // Not provided by the office: the current value is kept, this is not a change
  kept: boolean;
  // A known application date replaced by another one
  warning: boolean;
  classes?: ClassComparison[];
  unchangedClasses?: number[];
}

const DATE_FIELDS: SynchronizedField[] = ['applicationDate', 'publicationDate', 'registrationDate', 'oppositionEndDate', 'expirationDate'];

@Component({
  selector: 'app-trademark-content',
  templateUrl: './trademark-content.component.html',
  styleUrls: ['./trademark-content.component.scss']
})
export class TrademarkContentComponent implements OnInit, OnChanges {
  @Input() trademark: Trademark = {
    _id: undefined,
    identifierNumber: '',
    registrationNumber: undefined,
    applicationNumber: undefined,
    countryOfDesignation: '',
    kind: undefined,
    type: undefined,
    status: undefined,
    niceClassification: [],
    applicationDate: undefined,
    publicationDate: undefined,
    oppositionEndDate: undefined,
    registrationDate: undefined,
    expirationDate: undefined,
    reproductionURI: undefined,
    name: undefined,
    organizationId: undefined,
    clientReference: undefined,
  };
  @Input() refreshed?: SynchronizationCandidate;
  @Input() mode: "CONSULTATION" | "EDITION" | "COMPARISON" = "CONSULTATION";
  @Input() creation: boolean = false;
  @Input() productsServices: ProductService[] = [];

  differences: Difference[] = [];
  changesCount: number = 0;

  public newClass: {
    classNumber?: number,
    details?: string
  } = {
      classNumber: undefined,
      details: undefined
    };

  disabledNiceClasses: number[] = []

  @Output() reproductionChange = new EventEmitter<any>();

  propertyOffice?: PropertyOffice;

  trademarkTypes: any[] = [
    {
      value: TrademarkType.SHAPE_3D,
      label: "TRADEMARK.TYPES.SHAPE_3D",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.COLOUR,
      label: "TRADEMARK.TYPES.COLOUR",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.COLOURS_COMBINATION,
      label: "TRADEMARK.TYPES.COLOURS_COMBINATION",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.COMBINED,
      label: "TRADEMARK.TYPES.COMBINED",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.FIGURATIVE,
      label: "TRADEMARK.TYPES.FIGURATIVE",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.GENERAL,
      label: "TRADEMARK.TYPES.GENERAL",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.HOLOGRAM,
      label: "TRADEMARK.TYPES.HOLOGRAM",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.KENNFADEN,
      label: "TRADEMARK.TYPES.KENNFADEN",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.MOTION,
      label: "TRADEMARK.TYPES.MOTION",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.MULTIMEDIA,
      label: "TRADEMARK.TYPES.MULTIMEDIA",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.NUMBER,
      label: "TRADEMARK.TYPES.NUMBER",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.OLFACTORY,
      label: "TRADEMARK.TYPES.OLFACTORY",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.OTHER,
      label: "TRADEMARK.TYPES.OTHER",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.PACKING,
      label: "TRADEMARK.TYPES.PACKING",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.PATTERN,
      label: "TRADEMARK.TYPES.PATTERN",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.POSITION,
      label: "TRADEMARK.TYPES.POSITION",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.SOUND,
      label: "TRADEMARK.TYPES.SOUND",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.STYLIZED_CHARACTERS,
      label: "TRADEMARK.TYPES.STYLIZED_CHARACTERS",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.UNDEFINED,
      label: "TRADEMARK.TYPES.UNDEFINED",
      selected: false,
      disabled: false
    },
    {
      value: TrademarkType.WORD,
      label: "TRADEMARK.TYPES.WORD",
      selected: false,
      disabled: false
    }
  ];

  trademarkStatuses: Option[] = Object.entries(TrademarkStatus)
    .map(([key, value]) => ({
      label: this.translate.instant(`TRADEMARK.STATUS_${value}`),
      value: key,
    }));

  user?: User;
  language?: string;

  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    private productServiceService: ProductServiceService,
    private propertyOfficeService: PropertyOfficeService,
    private router: Router,
    private toastr: ToastrService,
    private territoryService: TerritoryService,
    private trademarkService: TrademarkService,
    private trademarkFamilyService: TrademarkFamilyService,
    private translate: TranslateService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
      this.language = this.user?.lang;
    });
  }

  ngOnInit(): void {
    this.getPropertyOffice();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && (changes["mode"] || changes["refreshed"]) && this.mode === "COMPARISON") {
      this.computeDifferences();
    }
    if (this.trademark && this.trademark.niceClassification) {
      this.disabledNiceClasses = this.trademark.niceClassification.map((niceClass) => Number(niceClass.classNumber));
    }
    if (changes["trademark"] && changes["trademark"].currentValue["propertyOffice"] && (!changes["trademark"].previousValue["propertyOffice"] || changes["trademark"].currentValue["propertyOffice"] !== changes["trademark"].previousValue["propertyOffice"])) {
      this.getPropertyOffice();
    }
  }

  getFamily() {
    if (this.trademark && this.trademark.family && this.trademark.family.ref) {
      return {
        exist: true,
        label: (this.trademark.family.ref as TrademarkFamily).label
      }
    } else {
      return {
        exist: false
      }
    }
  }

  toFamily() {
    if (this.trademark && this.trademark.family && this.trademark.family.ref) {
      this.router.navigateByUrl(`/trademarks/families/${(this.trademark.family.ref as TrademarkFamily)._id}`)
    }
  }

  async actionFamily() {
    if (this.trademark) {
      const config: MatDialogConfig = {
        panelClass: 'dialog-container',
        width: '480px',
        data: {
          trademark: this.trademark,
          family: this.trademark.family ? (this.trademark.family.ref as TrademarkFamily) : undefined
        }
      }
      const dialog = this.dialog.open(EditTrademarkFamilyDialogComponent, config);
      const result: { mode: "choose" | "new", newFamily?: Partial<TrademarkFamily>, family?: TrademarkFamily } = await firstValueFrom(dialog.afterClosed());
      try {
        if (result?.mode === "choose") {
          await this.trademarkService.updateFamily(this.trademark, result?.family?._id);
          this.trademark.family = {
            manual: true,
            ref: result.family
          };
          this.toastr.success(this.translate.instant('TRADEMARK_FAMILY.TRADEMARK_FAMILY_UPDATED', { name: this.trademark.name || this.trademark.identifierNumber }));
        } else if (result && result.mode === "new" && result.newFamily) {
          const toCreate = {
            ...result.newFamily,
            trademarks: [this.trademark._id]
          }
          const created = await this.trademarkFamilyService.create(toCreate);
          this.trademark.family = {
            manual: true,
            ref: created
          };
          this.toastr.success(this.translate.instant('TRADEMARK_FAMILY.FAMILY_CREATED', { name: created.label }));
          if (created && created._id) {
            await this.trademarkService.updateFamily(this.trademark, created._id);
            this.toastr.success(this.translate.instant('TRADEMARK_FAMILY.TRADEMARK_FAMILY_UPDATED', { name: this.trademark.name || this.trademark.identifierNumber }));
          }
        }

      } catch (err) {
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }


  async getPropertyOffice() {
    if (this.trademark && this.trademark.propertyOffice) {
      this.propertyOffice = await this.propertyOfficeService.retrieveOne(this.trademark.propertyOffice?.ref);
    }

  }

  validate(field: string) {
    if (['applicationDate', 'registrationDate'].includes(field) && (!this.trademark.applicationDate && !this.trademark.registrationDate)) {
      return {
        value: true,
        message: this.translate.instant(`TRADEMARK.APPLICATION_OR_REGISTRATION_DATE_REQUIRED`)
      }
    } else if (["registrationNumber", "applicationNumber"].includes(field) && (!this.trademark?.registrationNumber && !this.trademark?.applicationNumber)) {
      return {
        value: true,
        message: this.translate.instant(`TRADEMARK.IDENTIFIER_NUMBER_REQUIRED${this.creation ? '_CREATION' : ''}`)
      }
    } else if (field === "countryOfDesignation" && !this.trademark?.countryOfDesignation) {
      return {
        value: true,
        message: this.translate.instant('TRADEMARK.DESIGNATION_COUNTRY_REQUIRED')
      }
    } else if (field === "niceClassification" && (!this.trademark?.niceClassification || this.trademark.niceClassification.length === 0)) {
      return {
        value: true,
        message: this.translate.instant('TRADEMARK.NICE_CLASSIFICATION_REQUIRED')
      }
    } else if (field === "propertyOffice" && !this.trademark?.propertyOffice) {
      return {
        value: true,
        message: this.translate.instant('TRADEMARK.PROPERTY_OFFICE_REQUIRED')
      }
    } else if (field === "type" && !this.trademark?.type) {
      return {
        value: true,
        message: this.translate.instant('TRADEMARK.TYPE_REQUIRED')
      }
    } else {
      return {
        value: false,
        message: undefined
      }
    }
  }

  trademarkNiceClassNumbers(): number[] {
    return this.trademark?.niceClassification?.map(niceClass => Number(niceClass.classNumber)) || [];
  }


  getCountry() {
    if (this.trademark?.countryOfDesignation) {
      return this.translate.instant(`TERRITORIES.${this.territoryService.retrieveOne(this.trademark?.countryOfDesignation)?.id.toUpperCase()}`);
    } else {
      return '-';
    }
  }

  addClass() {
    if (this.trademark && this.newClass.classNumber) {
      this.trademark.niceClassification?.push({
        classNumber: this.newClass.classNumber,
        details: this.newClass.details
      });
      this.disabledNiceClasses.push(this.newClass.classNumber);
      this.newClass = {
        classNumber: undefined,
        details: undefined
      }
    }
  }

  removeClass(niceClass: { classNumber: number, details?: string }) {
    if (this.trademark) {
      this.trademark.niceClassification?.splice(this.trademark.niceClassification.indexOf(niceClass), 1);
    }
  }

  formatDate(date?: Moment) {
    return date ? moment(date).format('LL') : '-';
  }

  isSynchronizationSuccess(): boolean {
    return this.trademark.synchronization?.status === TrademarkSynchronizationStatus.SUCCESS;
  }

  synchronizationFailureKey(): string {
    return synchronizationFailureKey(this.trademark.synchronization?.failure);
  }

  formatSynchronizationDate(date?: string) {
    return date ? moment(date).format('LL') : '-';
  }

  _labelForProperty(property: string) {
    const labels: { [prop: string]: string } = {
      identifierNumber: 'TRADEMARK.IDENTIFIER_NUMBER',
      registrationNumber: 'TRADEMARK.REGISTRATION_NUMBER',
      applicationNumber: 'TRADEMARK.APPLICATION_NUMBER',
      countryOfDesignation: 'TRADEMARK.DESIGNATION_COUNTRY',
      kind: 'TRADEMARK.KIND',
      type: 'TRADEMARK.TYPE',
      niceClassification: 'TRADEMARK.NICE_CLASSIFICATION',
      applicationDate: 'TRADEMARK.APPLICATION_DATE',
      publicationDate: 'TRADEMARK.PUBLICATION_DATE',
      oppositionEndDate: 'TRADEMARK.OPPOSITION_END_DATE',
      registrationDate: 'TRADEMARK.REGISTRATION_DATE',
      expirationDate: 'TRADEMARK.EXPIRATION_DATE',
      reproductionURI: 'TRADEMARK.REPRODUCTION',
      name: 'TRADEMARK.NAME',
      status: 'TRADEMARK.STATUS',
      propertyOffice: 'ENTITIES.PROPERTY_OFFICE',
      applicationLanguageCode: 'TRADEMARK.APPLICATION_LANGUAGE_CODE'
    };
    return labels[property as keyof { [prop: string]: string }];
  }

  // Every field the synchronisation writes, as update() writes it, plus the ones it keeps for lack of an office value
  computeDifferences() {
    this.differences = [];
    this.changesCount = 0;
    if (!this.refreshed) return;
    const written = synchronizedValues(this.trademark, this.refreshed);
    for (const property of SYNCHRONIZED_FIELDS) {
      const kind = this._comparisonKind(property);
      const old = this.trademark[property];
      const base = { property, kind, label: this._labelForProperty(property), old, warning: false };
      if (!(property in written)) {
        if (this._hasValue(old)) {
          this.differences.push({ ...base, new: this.refreshed[property], kept: true });
        }
        continue;
      }
      const value = written[property];
      if (kind === 'classes') {
        const comparison = this._compareClasses(this.trademark.niceClassification ?? [], (value as NiceClass[]) ?? [], this.refreshed.niceClassification ?? []);
        if (comparison.classes.length > 0) {
          this.differences.push({
            ...base,
            new: value,
            kept: comparison.classes.every((niceClass) => niceClass.change === 'WORDING_KEPT'),
            classes: comparison.classes,
            unchangedClasses: comparison.unchanged
          });
        }
      } else if (!this._sameValue(kind, old, value)) {
        this.differences.push({
          ...base,
          new: value,
          kept: false,
          warning: property === 'applicationDate' && this._hasValue(old)
        });
      }
    }
    this.changesCount = this.differences.filter((difference) => !difference.kept).length;
  }

  _comparisonKind(property: SynchronizedField): ComparisonKind {
    if (DATE_FIELDS.includes(property)) return 'date';
    switch (property) {
      case 'status':
        return 'status';
      case 'propertyOffice':
        return 'office';
      case 'niceClassification':
        return 'classes';
      case 'reproductionURI':
        return 'image';
      default:
        return 'text';
    }
  }

  _hasValue(value: any): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }

  _sameValue(kind: ComparisonKind, old: any, value: any): boolean {
    if (!this._hasValue(old)) return false;
    switch (kind) {
      case 'date':
        return moment(old).format('YYYY-MM-DD') === moment(value).format('YYYY-MM-DD');
      case 'office':
        return old?.code === value?.code && (old?.name ?? '') === (value?.name ?? '');
      default:
        return String(old) === String(value);
    }
  }

  // Added, removed and reworded classes; a wording the office does not give is kept and is not a change
  _compareClasses(own: NiceClass[], written: NiceClass[], office: NiceClass[]): { classes: ClassComparison[], unchanged: number[] } {
    const sameClass = (a: NiceClass) => (b: NiceClass) => Number(a.classNumber) === Number(b.classNumber);
    const wording = (niceClass?: NiceClass) => (niceClass?.details ?? '').trim();
    const classes: ClassComparison[] = [];
    const unchanged: number[] = [];
    for (const niceClass of written) {
      const classNumber = Number(niceClass.classNumber);
      const ownClass = own.find(sameClass(niceClass));
      const officeClass = office.find(sameClass(niceClass));
      if (!ownClass) {
        classes.push({ classNumber, change: 'ADDED', new: niceClass.details });
      } else if (wording(ownClass) !== wording(niceClass)) {
        classes.push({ classNumber, change: 'WORDING', old: ownClass.details, new: niceClass.details });
      } else if (hasWording(ownClass) && !hasWording(officeClass)) {
        classes.push({ classNumber, change: 'WORDING_KEPT', old: ownClass.details });
      } else {
        unchanged.push(classNumber);
      }
    }
    for (const ownClass of own) {
      if (!written.some(sameClass(ownClass))) {
        classes.push({ classNumber: Number(ownClass.classNumber), change: 'REMOVED', old: ownClass.details });
      }
    }
    classes.sort((a, b) => a.classNumber - b.classNumber);
    unchanged.sort((a, b) => a - b);
    return { classes, unchanged };
  }

  onReproductionChange(reproduction?: File) {
    this.reproductionChange.emit(reproduction);
  }

  onPropertyOfficeChange(event: any) {
    if (event && event.value) {
      this.propertyOffice = event.value;
      this.trademark.propertyOffice = {
        ref: event.value._id,
        code: event.value.code,
        name: event.value.name,
        logo: event.value.logo
      }
    }
  }

  onTrademarkTypeChange(event: any) {
    if (event && event.value && event.value.value) {
      this.trademark.type = event.value.value;
    }
  }

  onTrademarkStatusChange(event: any) {
    if (event?.value?.value) {
      this.trademark.status = event.value.value;
    }
  }

  getProductsServices(classNumber: Number) {
    return this.productsServices.filter((ps) => ps.classNumber === classNumber)
  }


  getProductServiceLabel(productService: ProductService) {
    return this.productServiceService.getLabel(productService, this.language);
  }



  isDisplayedInApplicationLanguage() {
    return this.trademark.applicationLanguageCode?.toLowerCase() === this.language;
  }

  toggleApplicationLanguage() {
    if (this.language === this.trademark.applicationLanguageCode) {
      this.language = this.user?.lang;
    } else {
      this.language = this.trademark.applicationLanguageCode;
    }
  }

}
